import { type Message } from "@/pages/chat/types/chat"

interface WelcomeSpreadResponse {
    message: string
    sessionId?: string
    timestamp: Date
    spreadId?: string
    step?: number
}

interface SendMessageRequest {
    content: string
    sessionId?: string
    step?: number
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'
const API_KEY = import.meta.env.VITE_WELCOME_SPREAD_API_KEY || 'welcome-spread-2025-secure-key'

const _sendMessage = async (request: SendMessageRequest): Promise<WelcomeSpreadResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/welcome-spread/message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY
            },
            body: JSON.stringify(request)
        })

        if (!response.ok) {
            throw new Error("Failed to send message")
        }

        return await response.json()
    } catch (error) {
        console.error("Error in _sendMessage:", error)
        throw error
    }
}

const _createSession = async (): Promise<{ sessionId: string }> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/welcome-spread/session`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY
            }
        })

        if (!response.ok) {
            throw new Error("Failed to create session")
        }

        return await response.json()
    } catch (error) {
        console.error("Error in _createSession:", error)
        throw error
    }
}

const _getSessionMessages = async (sessionId: string): Promise<Message[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/welcome-spread/session/${sessionId}/messages`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY
            }
        })

        if (!response.ok) {
            throw new Error("Failed to get session messages")
        }

        return await response.json()
    } catch (error) {
        console.error("Error in _getSessionMessages:", error)
        throw error
    }
}

export class WelcomeSpreadService {
    private sessionId: string | null = null
    private isLoading: boolean = false
    private error: string | null = null

    setError(error: string | null) {
        this.error = error
    }

    getError(): string | null {
        return this.error
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading
    }

    getIsLoading(): boolean {
        return this.isLoading
    }

    getSessionId(): string | null {
        return this.sessionId
    }

    async createSession(): Promise<string | null> {
        this.setIsLoading(true)
        this.setError(null)
        try {
            const result = await _createSession()
            this.sessionId = result.sessionId
            return this.sessionId
        } catch (err) {
            this.setError((err as Error).message)
            return null
        } finally {
            this.setIsLoading(false)
        }
    }

    async sendMessage(content: string, step?: number): Promise<WelcomeSpreadResponse | null> {
        this.setIsLoading(true)
        this.setError(null)
        try {
            const request: SendMessageRequest = {
                content,
                sessionId: this.sessionId || undefined,
                ...(step && { step })
            }
            const response = await _sendMessage(request)

            // Update session ID if returned from server
            if (response.sessionId && !this.sessionId) {
                this.sessionId = response.sessionId
            }

            return response
        } catch (err) {
            this.setError((err as Error).message)
            return null
        } finally {
            this.setIsLoading(false)
        }
    }

    async getSessionMessages(sessionId?: string): Promise<Message[] | null> {
        const targetSessionId = sessionId || this.sessionId
        if (!targetSessionId) {
            this.setError("No active session")
            return null
        }

        this.setIsLoading(true)
        this.setError(null)
        try {
            return await _getSessionMessages(targetSessionId)
        } catch (err) {
            this.setError((err as Error).message)
            return null
        } finally {
            this.setIsLoading(false)
        }
    }

    clearSession() {
        this.sessionId = null
        this.error = null
    }
}

export const welcomeSpreadService = new WelcomeSpreadService()
